import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { Watch } from "@/decorators/watch";

import { otherCountry } from "@/internals/i18n/data";

import AtlasSelect from "@/components/form/atlas-select/atlas-select";
import type { SelectOption } from "@/components/form/atlas-select/types";

import styles from "./atlas-phone-country-select.scss";

import "@/components/display/atlas-icon/atlas-icon";
import "@/components/form/atlas-phone/atlas-phone-flag";
import "@/components/form/atlas-input/atlas-input";

/**
 * @dependency atlas-phone-flag
 * @dependency atlas-input
 * @dependency atlas-icon
 *
 * @tag atlas-phone-country-select
 */
@customElement("atlas-phone-country-select")
export default class AtlasPhoneCountrySelect extends AtlasSelect {
    static styles = styles;

    @state() private _isOnHover = false;

    @state() private _isOnFocus = false;

    /** @internal */
    @Watch("_isDropdownOpen", false)
    public onDropdownOpenChange() {
        this.classList.toggle("collapsed", !this._isDropdownOpen);
    }

    /* @override */
    protected onInputFocus() {
        this._selectInput._input.select();
        this._isOnFocus = true;
    }

    /* @override */
    protected onInputBlur() {
        this._isOnFocus = false;

        setTimeout(() => {
            this.reportValidity();
        }, 200);
    }

    /* @override */
    protected onDropdownOpen() {
        this._isDropdownOpen = true;

        if (!this._hasDoneFirstSearch) {
            this.searchOptions();
        }

        this._selectInput.focus();
    }

    private onMouseOver() {
        this._isOnHover = true;
    }

    private onMouseOut() {
        this._isOnHover = false;
    }

    private renderChevronIcon() {
        const iconClass = {
            "input-icon": true,
            "has-click": true
        };

        return html` <atlas-icon name="chevron-down" size="2x" class=${classMap(iconClass)}></atlas-icon> `;
    }

    private renderSelectedOption() {
        const selectedOption = this.getSelectedOptions() as SelectOption;
        const selectedCountry = selectedOption?.customProperties?.countryCode || otherCountry.iso2;

        return html`
            <atlas-phone-flag
                class="atlas-phone-flag"
                countryCode=${selectedCountry}
                ?data-on-hover=${this._isOnHover}
                ?disabled=${this.disabled}
                ?readonly=${this.readonly}
            ></atlas-phone-flag>
        `;
    }

    private renderSelectedCountry() {
        const formControlClass = {
            "atlas-phone-country-select": true,
            "form-control": true,
            "with-height": true,
            [`form-control-${this.size}`]: !!this.size,
            "disabled": this.disabled,
            "readonly": this.readonly,
            "has-hover": this._isOnHover,
            "has-focus": this._isOnFocus
        };

        return html`
            <div
                class=${classMap(formControlClass)}
                data-atlas-dropdown="select-dropdown"
                @mouseover=${this.onMouseOver}
                @mouseout=${this.onMouseOut}
            >
                ${this.renderSelectedOption()} ${this.renderChevronIcon()}
            </div>
        `;
    }

    protected renderInput() {
        return html`
            <atlas-input
                class="atlas-select-input"
                @atlas-input-change=${this.onInputChange}
                @atlas-input-focus=${this.onInputFocus}
                @atlas-input-blur=${this.onInputBlur}
                @keydown=${this.onInputKeyDown}
                ?disabled=${this.disabled}
            ></atlas-input>
        `;
    }

    /** @internal */
    public render() {
        return html`
            ${this.renderSelectedCountry()} ${this.renderInput()} ${this.renderSelectDropdown()}
            <slot @slotchange=${this.loadSlottedOptions}></slot>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-phone-country-select": AtlasPhoneCountrySelect;
    }
}
